import React, { useState, useRef, useEffect } from "react";
import { Box, Dialog, DialogContent } from "@material-ui/core";
import { validateTextTypeInputs, validateTextTypeInputsBeforeSaving } from "../RowId/Validations";
import { commonInputStyles, ColumnName, SavingSpinnerForColumnEditors, ActionButtonsForColumnEditors, handleClose, ErrorMessageViewer } from "./ColumnEditorFactory";

const MultiLineReusableEditor = ({ 
    initialValue = "", 
    open, 
    data, 
    columnName, 
    column, 
    onSave, 
    onCancel 
}) => {
    const [value, setValue] = useState(initialValue);
    const inputRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const [validationError, setValidationError] = useState({ isInvalid: false, message: "" });
    const [isSaving, setIsSaving] = useState(false);

    const columnTypeId = String(column?.column_type_id || '1');
    const isEmailType = columnTypeId === "14";

    function getSizes(columnTypeId, hasError = false) {
        switch (String(columnTypeId)) {
            case "8":
                return hasError
                    ? { inputHeight: "250px", inputContainerHeight: "420px" }
                    : { inputHeight: "250px", inputContainerHeight: "400px" };

            case "20":
                return hasError
                    ? { inputHeight: "44px", inputContainerHeight: "195px" }
                    : { inputHeight: "44px", inputContainerHeight: "170px" };

            default:
                return hasError
                    ? { inputHeight: "45px", inputContainerHeight: "225px" }
                    : { inputHeight: "45px", inputContainerHeight: "200px" };
        }
    }

    const { inputHeight, inputContainerHeight } = getSizes(columnTypeId, validationError?.isInvalid);

    useEffect(() => {
        if (open) {
            setValue(initialValue ?? '');
            setValidationError({ isInvalid: false, message: "" });
            const timer = setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    const len = inputRef.current.value.length;
                    inputRef.current.setSelectionRange(len, len);
                }
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [initialValue, open, columnTypeId]);

    useEffect(() => {
        if (!isEmailType) return;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            const error = validateTextTypeInputs(value, column, isEmailType);
            setValidationError(error);
        }, 500);

        return () => {
            clearTimeout(debounceTimerRef.current);
            setValidationError({ isInvalid: false, message: "" });
        }
    }, [value, isEmailType]);

    const handleChange = (e) => {
        setValue(e.target.value);
        if (validationError.isInvalid) {
            setValidationError({ isInvalid: false, message: "" });
        }
    };

    const handleSave = async() => {
        try {
            if ((isEmailType && validationError.isInvalid) || value === initialValue) {
                onCancel();
                return;
            }

            const validation = validateTextTypeInputs(value, column);
            if (validation.isInvalid) {
                setValidationError(validation);
                return;
            }

            const isUpdateAllowed = validateTextTypeInputsBeforeSaving(value, column, data);
            if (isUpdateAllowed.isInvalid) {
                setValidationError(isUpdateAllowed);
                return;
            }
            setIsSaving(true);
            await onSave(value, initialValue, column);
        } catch (err) {
            console.log('Error saving value.', err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && columnTypeId != "8") {
            e.preventDefault();
            handleSave();
        } else if (e.shiftKey && e.key === "Enter" && columnTypeId == "8") {
            e.preventDefault();
            handleSave();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={() => handleClose(value, initialValue, onCancel, validationError, setValidationError)}
            fullWidth 
            PaperProps={{
                style: {
                    width: String(columnTypeId) === '20' ? '420px' : '600px',
                    height: inputContainerHeight,
                    borderRadius: '8px',
                    transition: "height 0.2s ease"
                },
            }}
        >
            <DialogContent>
                <Box display="flex" flexDirection="column" style={{ width: "100%", gap: 10 }}>
                    {columnName && <ColumnName columnName={columnName}/>}
                    {(columnTypeId == "1" || columnTypeId == "14"|| columnTypeId == "20" || columnTypeId == "18" || columnTypeId == "24") 
                    ?
                        <input
                            ref={inputRef}
                            value={value}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            disabled={isSaving}
                            style={{
                                ...commonInputStyles,
                                height: inputHeight,
                                resize: "none",
                                overflowY: "auto",
                                cursor: 'text'
                            }}
                            {...((columnTypeId == '1' || columnTypeId == '20') ? { maxLength: column?.normalizedColumnOptions?.maxLength ?? 100 } : {})}
                        />
                    :
                        <textarea
                            ref={inputRef}
                            value={value}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            disabled={isSaving}
                            style={{
                                ...commonInputStyles,
                                height: inputHeight,
                                resize: "none",
                                overflowY: "auto",
                                cursor: 'text'
                            }}
                        />
                    }
                    {columnTypeId == "8" && (
                        <div className="multiLineExtraText" style={{paddingBottom: 0, paddingTop:0}}>
                            Press Shift+Enter to save
                        </div>
                    )}

                    {validationError.isInvalid && (<ErrorMessageViewer errMsg={validationError.message} />)}

                    <ActionButtonsForColumnEditors handleSave={handleSave} handleCancel={() => handleClose(value, initialValue, onCancel, validationError, setValidationError)} isSaveDisabled={validationError.isInvalid || isSaving} isCloseDisabled={isSaving} />

                    {isSaving && (<SavingSpinnerForColumnEditors />)}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default MultiLineReusableEditor;